import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api.ts';
import { mapService } from '../services/mapService.ts';
import languageService from '../services/languageService.ts';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  profile_picture_url?: string;
  language_preference?: string;
  created_at?: string;
  updated_at?: string;
}

interface Customer {
  id: string;
  name: string;
  status: string;
  subscription_plan?: string;
  subscriptionActive: boolean;
  subscription_start_date?: string;
  subscription_end_date?: string;
  subscription_canceled_at?: string;
}

interface AuthContextType {
  user: User | null;
  customer: Customer | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

interface RegisterData {
  customerName: string;
  customerAddress: string;
  userName: string;
  email: string;
  password: string;
  verificationCode: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!user;
  
  // Debug logging
  useEffect(() => {
    console.log('AuthContext state changed:', {
      hasToken: !!token,
      hasUser: !!user,
      hasCustomer: !!customer,
      isAuthenticated,
      isLoading
    });
  }, [token, user, customer, isAuthenticated, isLoading]);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        const storedCustomer = localStorage.getItem('customer');

        console.log('AuthContext initialization:', {
          hasToken: !!storedToken,
          hasUser: !!storedUser,
          hasCustomer: !!storedCustomer,
          userAgent: navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') ? 'Safari' : 'Other'
        });

        if (storedToken && storedUser && storedCustomer) {
          console.log('Setting auth state from localStorage');
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          setCustomer(JSON.parse(storedCustomer));

          // Set user's language preference on page refresh
          languageService.reinitializeLanguage(parsedUser);

          // Verify token is still valid (but don't clear on failure immediately)
          try {
            const response = await authAPI.verifyToken();
            console.log('Token verification successful');
            
            // Update user data with fresh data from server (including language preference)
            if (response.data.user) {
              console.log('Updating user data with fresh server data:', response.data.user);
              setUser(response.data.user);
              // Re-initialize language with fresh user data
              languageService.initializeLanguage(response.data.user);
            }
            
            // Preload Google Maps API in the background for faster map loading
            mapService.preloadGoogleMaps().catch(error => {
              console.warn('Failed to preload Google Maps:', error);
              // Don't throw - this is a background optimization
            });
          } catch (error) {
            console.error('Token verification failed:', error);
            console.log('Keeping auth state despite verification failure - will retry on next API call');
            // Don't clear storage immediately - let the user try to use the app
            // The API interceptor will handle clearing on actual API failures
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear any corrupted data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('customer');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Language initialization effect - runs when user data changes
  useEffect(() => {
    if (user) {
      console.log('User data changed, re-initializing language:', user.language_preference);
      console.log('Full user object:', user);
      languageService.reinitializeLanguage(user);
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      const { token: newToken, user: newUser, customer: newCustomer } = response.data;

      // Store in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('customer', JSON.stringify(newCustomer));

      // Update state
      setToken(newToken);
      setUser(newUser);
      setCustomer(newCustomer);

      // Set user's language preference
      languageService.reinitializeLanguage(newUser);

      // Preload Google Maps API in the background for faster map loading
      mapService.preloadGoogleMaps().catch(error => {
        console.warn('Failed to preload Google Maps:', error);
        // Don't throw - this is a background optimization
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await authAPI.register(data);
      const { token: newToken, user: newUser, customer: newCustomer } = response.data;

      // Store in localStorage
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('customer', JSON.stringify(newCustomer));

      // Update state
      setToken(newToken);
      setUser(newUser);
      setCustomer(newCustomer);

      // Preload Google Maps API in the background for faster map loading
      mapService.preloadGoogleMaps().catch(error => {
        console.warn('Failed to preload Google Maps:', error);
        // Don't throw - this is a background optimization
      });
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('customer');

    // Clear state
    setToken(null);
    setUser(null);
    setCustomer(null);
  };

  const refreshProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      const { user: newUser, customer: newCustomer } = response.data;

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('customer', JSON.stringify(newCustomer));

      // Update state
      setUser(newUser);
      setCustomer(newCustomer);
    } catch (error) {
      console.error('Profile refresh error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    customer,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
